import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Mart {
  readonly martId: string;
  readonly martCode: string;
  readonly martName: string;
  readonly martType: string;
  readonly isPublished: boolean;
}

/** M26 — Analytics Dashboard (§31). Lists data marts feeding analytics. */
export function M26AnalyticsDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Mart[]>([]);
  const [total, setTotal] = useState(0);
  const [martType, setMartType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = martType ? `?martType=${encodeURIComponent(martType)}` : '';
    apiRequest<{ data: Mart[]; total: number }>(`/v1/dwh/marts${q}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, martType]);

  return (
    <main aria-label="analytics-dashboard">
      <h1>{tr('m26.analytics.title')}</h1>
      <label htmlFor="mart-filter">{tr('m26.analytics.martTypeLabel')}</label>
      <select id="mart-filter" value={martType} onChange={(e) => setMartType(e.target.value)}>
        {['', 'FINANCE', 'OPERATIONS', 'COMPLIANCE', 'VENDOR', 'TERRITORY', 'EXECUTIVE', 'CAMPAIGN', 'PERFORMANCE'].map((t) => <option key={t || 'all'} value={t}>{t || tr('m26.analytics.allMarts')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m26.analytics.loading')}</p> : (
        <>
          <p aria-label="mart-count">{tr('m26.analytics.count', { count: total })}</p>
          <table aria-label="mart-table">
            <thead><tr><th>{tr('m26.analytics.colCode')}</th><th>{tr('m26.analytics.colName')}</th><th>{tr('m26.analytics.colType')}</th><th>{tr('m26.analytics.colStatus')}</th></tr></thead>
            <tbody>{rows.map((m) => <tr key={m.martId}><td>{m.martCode}</td><td>{m.martName}</td><td>{m.martType}</td><td>{m.isPublished ? tr('m26.analytics.published') : tr('m26.analytics.draft')}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
